import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaFilter, FaSearch, FaDrumstickBite, FaLeaf } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';

/** Liste API (last_price_per_share) veya animals.price ile hisse başı fiyat */
const getDisplayPricePerShare = (animal) => {
  if (!animal) return null;
  const tryParse = (v) => {
    if (v === undefined || v === null || v === '') return NaN;
    const n = typeof v === 'number' ? v : parseFloat(String(v).trim().replace(/\s/g, '').replace(',', '.'));
    return Number.isFinite(n) && n > 0 ? n : NaN;
  };
  for (const key of ['last_price_per_share', 'lastPricePerShare', 'price']) {
    const n = tryParse(animal[key]);
    if (!Number.isNaN(n)) return n;
  }
  return null;
};

/** Virgülden sonra 2 hane; kuruşları yuvarlamaz, 2 haneye keser (örn. 24.142,85). */
const formatPricePerShareTry = (numeric) => {
  if (numeric == null || !Number.isFinite(Number(numeric))) return null;
  const truncated = Math.floor(Number(numeric) * 100) / 100;
  return truncated.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/**
 * Panelde "Satışa Ekle" sonrası durum genelde Satışta olur; hisse satışı / tetikleyici
 * bazen Satıldı yazar ama kalan hisse vardır. Public liste: ilan + satılabilir hisse.
 */
function shouldShowAnimalOnPublicPage(animal) {
  if (!animal) return false;
  const st = String(animal.status ?? '').trim();
  const rem = animal.remaining_shares != null ? Number(animal.remaining_shares) : NaN;
  const sold = animal.sold_shares != null ? Number(animal.sold_shares) : 0;
  const priceRaw = animal.price;
  const priceNum =
    priceRaw != null && priceRaw !== ''
      ? parseFloat(String(priceRaw).replace(/\s/g, '').replace(',', '.'))
      : NaN;

  if (st === 'Satışta' || st === 'onsale') {
    if (Number.isFinite(rem) && rem <= 0) return false;
    return true;
  }

  if (sold > 0 && Number.isFinite(rem) && rem > 0) return true;

  if ((st === 'active' || st === 'Aktif') && Number.isFinite(priceNum) && priceNum > 0) return true;

  return false;
}

const AnimalsContainer = styled.div`
  padding-top: 120px;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, var(--primary-color) 0%, #e67e00 100%);
  color: var(--white-color);
  padding: 100px 0;
  text-align: center;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  opacity: 0.9;
  max-width: 600px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const Section = styled.section`
  padding: 80px 0;
  
  @media (max-width: 768px) {
    padding: 60px 0;
  }
`;

const FilterSection = styled.div`
  background: var(--light-gray);
  padding: 40px 0;
  margin-bottom: 40px;
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchInput = styled.div`
  position: relative;
  flex: 1;
  min-width: 300px;
  
  @media (max-width: 768px) {
    min-width: auto;
  }
`;

const SearchField = styled.input`
  width: 100%;
  padding: 15px 45px 15px 15px;
  border: 2px solid var(--light-gray);
  border-radius: var(--border-radius);
  font-size: 16px;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--secondary-color);
`;

const FilterSelect = styled.select`
  padding: 15px;
  border: 2px solid var(--light-gray);
  border-radius: var(--border-radius);
  font-size: 16px;
  background: var(--white-color);
  min-width: 150px;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const AnimalsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 30px;
`;

const AnimalCard = styled(motion.div)`
  background: var(--white-color);
  border-radius: var(--border-radius);
  overflow: hidden;
  box-shadow: var(--shadow);
  transition: var(--transition);
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15);
  }
`;

const AnimalImage = styled.div`
  height: 200px;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: var(--transition);
  }
  
  ${AnimalCard}:hover & img {
    transform: scale(1.1);
  }
`;

const AnimalInfo = styled.div`
  padding: 20px;
`;

const AnimalType = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  
  svg {
    color: var(--primary-color);
  }
`;

const AnimalTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 10px;
  color: var(--secondary-color);
`;

const AnimalDetails = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 15px;
`;

const DetailItem = styled.div`
  font-size: 14px;
  color: var(--secondary-color);
  
  span {
    font-weight: 600;
  }
`;

const AnimalPrice = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 15px;
`;

const ViewButton = styled(motion.button)`
  background: var(--primary-color);
  color: var(--white-color);
  padding: 12px 25px;
  border-radius: var(--border-radius);
  font-size: 14px;
  font-weight: 600;
  width: 100%;
  transition: var(--transition);
  
  &:hover {
    background: #e67e00;
  }
`;

const AnimalsPage = () => {
  console.log('🚀 AnimalsPage bileşeni render edildi!');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [animals, setAnimals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [selectedAnimal, setSelectedAnimal] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const fetchAnimals = async () => {
      console.log('🔄 Hayvanlar yükleniyor...');
      setLoading(true);
      setError('');
      try {
        console.log('📡 API isteği gönderiliyor:', API_ENDPOINTS.ANIMALS);
        const response = await fetch(API_ENDPOINTS.ANIMALS, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        console.log('📥 API yanıtı alındı:', response.status, response.statusText);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('❌ API hatası:', response.status, errorText);
          throw new Error(`Hayvanlar yüklenemedi: ${response.status} ${response.statusText}`);
        }
        
        const data = await response.json();
        console.log('📊 API\'den gelen hayvanlar:', data);
        console.log('📊 Hayvan sayısı:', Array.isArray(data) ? data.length : 0);
        
        // Veri kontrolü yap
        if (Array.isArray(data)) {
          setAnimals(data);
          console.log('✅ Hayvanlar state\'e yüklendi');
        } else {
          console.warn('⚠️ API\'den gelen veri array değil:', data);
          setAnimals([]);
        }
      } catch (err) {
        console.error('❌ Hata oluştu:', err);
        setError(`Hayvanlar yüklenirken hata oluştu: ${err.message}`);
        setAnimals([]); // Hata durumunda boş array set et
      } finally {
        setLoading(false);
        console.log('🏁 Yükleme tamamlandı');
      }
    };
    
    // Sadece component mount olduğunda çalıştır
    fetchAnimals();
  }, []);

  const filteredAnimals = animals.filter(animal => {
    // Arama terimi kontrolü - ID, Irk, Küpe No, Tür veya Cins
    const searchFields = [
      animal.id?.toString() || '',
      animal.breed || '',
      animal.ear_tag || '',
      animal.type || '',
      animal.gender || ''
    ].join(' ').toLowerCase();
    
    const matchesSearch = searchTerm === '' || searchFields.includes(searchTerm.toLowerCase());
    const matchesFilter = filterType === 'all' || animal.type === filterType;
    const isForSale = shouldShowAnimalOnPublicPage(animal);

    return matchesSearch && matchesFilter && isForSale;
  });

  const animalTypes = [
    { value: 'all', label: 'Tümü', icon: <FaDrumstickBite /> },
    { value: 'Büyükbaş', label: 'Büyükbaş', icon: <FaDrumstickBite /> },
    { value: 'Küçükbaş', label: 'Küçükbaş', icon: <FaLeaf /> }
  ];

  return (
    <AnimalsContainer>
      <HeroSection>
        <Container>
          <HeroTitle>Hayvanlarımız</HeroTitle>
          <HeroSubtitle>
            Sağlıklı ve kaliteli hayvanlarımızı inceleyin
          </HeroSubtitle>
        </Container>
      </HeroSection>

      <FilterSection>
        <Container>
          <FilterContainer>
            <SearchInput>
              <SearchField
                type="text"
                placeholder="ID, Irk, Küpe No, Tür veya Cins ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <SearchIcon>
                <FaSearch />
              </SearchIcon>
            </SearchInput>
            
            <FilterSelect
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="all">Tüm Türler</option>
              <option value="Büyükbaş">Büyükbaş</option>
              <option value="Küçükbaş">Küçükbaş</option>
            </FilterSelect>
          </FilterContainer>
        </Container>
      </FilterSection>

      <Section>
        <Container>
          <AnimalsGrid>
            {loading ? (
              <p>Yükleniyor...</p>
            ) : error ? (
              <p>{error}</p>
            ) : filteredAnimals.length === 0 ? (
              <p>Aradığınız kriterlere uygun hayvan bulunamadı.</p>
            ) : (
              filteredAnimals.map((animal, index) => {
                // Hayvan objesi kontrolü
                if (!animal) {
                  console.warn('Geçersiz hayvan objesi:', animal);
                  return null;
                }
                
                // Yaş hesaplama
                let age = '-';
                if (animal.birth_date) {
                  try {
                    const birthDate = new Date(animal.birth_date);
                    if (!isNaN(birthDate.getTime())) {
                      const birthYear = birthDate.getFullYear();
                      const nowYear = new Date().getFullYear();
                      age = (nowYear - birthYear) + ' yıl';
                    }
                  } catch (error) {
                    console.warn('Yaş hesaplama hatası:', error);
                    age = '-';
                  }
                }
                return (
                  <AnimalCard
                    key={animal.id || index}
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true }}
                  >
                    <AnimalImage>
                      {animal.image_url ? (
                        <img src={API_ENDPOINTS.UPLOADS(animal.image_url)} alt={animal.breed || animal.ear_tag || 'Hayvan'} />
                      ) : (
                        <div style={{height: 200, background: '#eee'}} />
                      )}
                    </AnimalImage>
                    <AnimalInfo>
                      <AnimalType>
                        {animal.type === 'Büyükbaş' ? <FaDrumstickBite /> : <FaLeaf />}
                        <span>{animal.type}</span>
                      </AnimalType>
                      <AnimalTitle>{animal.breed || animal.ear_tag || 'Hayvan'}</AnimalTitle>
                      <AnimalDetails>
                        <DetailItem>
                          <span>Yaş:</span> {age}
                        </DetailItem>
                        <DetailItem>
                          <span>Ağırlık:</span> {animal.last_weight ? `${animal.last_weight} kg` : animal.start_weight ? `${animal.start_weight} kg` : '-'}
                        </DetailItem>
                        <DetailItem>
                          <span>Hisse Başı Fiyat:</span>{' '}
                          {(() => {
                            const p = getDisplayPricePerShare(animal);
                            const s = formatPricePerShareTry(p);
                            return s != null ? `₺${s}` : '-';
                          })()}
                        </DetailItem>
                      </AnimalDetails>
                      <AnimalPrice>
                        {(() => {
                          const p = getDisplayPricePerShare(animal);
                          const s = formatPricePerShareTry(p);
                          return s != null ? `₺${s}` : '';
                        })()}
                      </AnimalPrice>
                      <ViewButton
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => { setSelectedAnimal(animal); setShowModal(true); }}
                      >
                        Detayları Görüntüle
                      </ViewButton>
                    </AnimalInfo>
                  </AnimalCard>
                );
              })
            )}
          </AnimalsGrid>
        </Container>
      </Section>
      {showModal && selectedAnimal && (
  <div style={{
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
  }}>
    <div style={{ background: '#fff', borderRadius: 16, maxWidth: 600, width: '100%', padding: 32, position: 'relative' }}>
      <button onClick={() => setShowModal(false)} style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#888' }}>×</button>
      <h2 style={{ color: 'var(--primary-color)', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 10 }}>
        <span role="img" aria-label="cow">🐄</span> Hayvan Detayları
      </h2>
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        {selectedAnimal.image_url ? (
          <img src={API_ENDPOINTS.UPLOADS(selectedAnimal.image_url)} alt="Hayvan Fotoğrafı" style={{ maxWidth: '100%', maxHeight: 220, borderRadius: 12, objectFit: 'cover' }} />
        ) : (
          <div style={{ height: 120, background: '#eee', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa' }}>Hayvan Fotoğrafı</div>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, background: '#f8f9fa', borderRadius: 10, padding: 24, fontSize: '1rem', color: 'var(--secondary-color)', marginBottom: 24 }}>
        <div><strong>ID:</strong> #{selectedAnimal.id}</div>
        <div><strong>Tür:</strong> {selectedAnimal.type || '-'}</div>
        <div><strong>Irk:</strong> {selectedAnimal.breed || '-'}</div>
        <div><strong>Cins:</strong> {selectedAnimal.gender || '-'}</div>
        <div><strong>Küpe/Hayvan No:</strong> {selectedAnimal.ear_tag || '-'}</div>
        <div><strong>Güncel Ağırlık:</strong> {selectedAnimal.last_weight ? `${selectedAnimal.last_weight} kg` : selectedAnimal.start_weight ? `${selectedAnimal.start_weight} kg` : '-'}</div>
        <div><strong>Hisse Başı Fiyat:</strong>{' '}
          {(() => {
            const p = getDisplayPricePerShare(selectedAnimal);
            const s = formatPricePerShareTry(p);
            return s != null ? `₺${s}` : '-';
          })()}
        </div>
        <div><strong>Doğum Tarihi:</strong> {selectedAnimal.birth_date ? (() => {
          try {
            const date = new Date(selectedAnimal.birth_date);
            return isNaN(date.getTime()) ? 'Belirtilmemiş' : date.toLocaleDateString('tr-TR');
          } catch (error) {
            return 'Belirtilmemiş';
          }
        })() : 'Belirtilmemiş'}</div>
        <div><strong>Giriş Tarihi:</strong> {selectedAnimal.entry_date ? (() => {
          try {
            const date = new Date(selectedAnimal.entry_date);
            return isNaN(date.getTime()) ? '-' : date.toLocaleDateString('tr-TR');
          } catch (error) {
            return '-';
          }
        })() : '-'}</div>
        <div><strong>Durum:</strong> <span style={{ padding: '4px 8px', borderRadius: '12px', fontSize: '0.9rem', background: selectedAnimal.status === 'Satışta' ? '#ffeeba' : '#d4edda', color: selectedAnimal.status === 'Satışta' ? '#856404' : '#155724' }}>{selectedAnimal.status === 'Satışta' ? 'Satışta' : 'Aktif'}</span></div>
        <div style={{ gridColumn: '1 / -1' }}><strong>Notlar:</strong><br /><span style={{ display: 'inline-block', marginTop: 6, background: '#fff', borderRadius: 6, padding: 10, minHeight: 32, color: '#555', fontStyle: selectedAnimal.notes ? 'normal' : 'italic' }}>{selectedAnimal.notes || 'Belirtilmemiş'}</span></div>
        <div><strong>Alındığı Kişi/Firma:</strong> {selectedAnimal.supplier || '-'}</div>
      </div>
      <button
        style={{ background: 'var(--primary-color)', color: '#fff', border: 'none', borderRadius: 8, padding: '14px 32px', fontWeight: 600, fontSize: 18, cursor: 'pointer', display: 'block', margin: '0 auto' }}
        onClick={() => { setShowModal(false); navigate('/iletisim'); }}
      >
        İletişime Geç
      </button>
    </div>
  </div>
)}
    </AnimalsContainer>
  );
};

export default AnimalsPage; 